import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

export type RagLightboxItem = {
  src: string;
  label?: string;
};

/** Fullscreen diagram viewer (shared by answer text and image rail). */
export function RagImageLightbox({
  src,
  gallery,
  onClose,
}: {
  src: string | null;
  gallery?: RagLightboxItem[];
  onClose: () => void;
}) {
  const items = useMemo(
    () => (gallery?.length ? gallery : src ? [{ src, label: undefined }] : []),
    [gallery, src]
  );

  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!src || !items.length) return;
    const nextIndex = items.findIndex((item) => item.src === src);
    setIndex(nextIndex >= 0 ? nextIndex : 0);
  }, [src, items]);

  const active = items[index];
  const hasMultiple = items.length > 1;

  const goPrev = useCallback(() => {
    setIndex((i) => (i - 1 + items.length) % items.length);
  }, [items.length]);

  const goNext = useCallback(() => {
    setIndex((i) => (i + 1) % items.length);
  }, [items.length]);

  useEffect(() => {
    if (!src) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && hasMultiple) goPrev();
      if (e.key === "ArrowRight" && hasMultiple) goNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [src, onClose, hasMultiple, goPrev, goNext]);

  if (!src || !active) return null;

  return (
    <Box
      onClick={onClose}
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: (theme) => theme.zIndex.modal + 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "rgba(0,0,0,0.82)",
        p: 2,
      }}
    >
      <IconButton
        onClick={onClose}
        aria-label="Close"
        sx={{
          position: "absolute",
          top: 16,
          right: 16,
          color: "common.white",
          bgcolor: "rgba(255,255,255,0.15)",
          "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
        }}
      >
        <CloseIcon />
      </IconButton>

      {active.label && (
        <Typography
          variant="bodySmall"
          sx={{
            position: "absolute",
            top: 20,
            left: 24,
            color: "common.white",
            fontWeight: 600,
          }}
        >
          {active.label}
        </Typography>
      )}

      {hasMultiple && (
        <>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            aria-label="Previous diagram"
            sx={{
              position: "absolute",
              left: 16,
              color: "common.white",
              bgcolor: "rgba(255,255,255,0.15)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            aria-label="Next diagram"
            sx={{
              position: "absolute",
              right: 56,
              color: "common.white",
              bgcolor: "rgba(255,255,255,0.15)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.3)" },
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </>
      )}

      <Box
        component="img"
        src={active.src}
        alt={active.label ?? "Diagram"}
        onClick={(e) => e.stopPropagation()}
        sx={{
          maxHeight: "90vh",
          maxWidth: "90vw",
          borderRadius: 2,
          boxShadow: 24,
          objectFit: "contain",
        }}
      />

      <Typography
        variant="labelSmall"
        sx={{
          position: "absolute",
          bottom: 20,
          color: "rgba(255,255,255,0.75)",
        }}
      >
        {hasMultiple ? `${index + 1} / ${items.length} · ` : ""}
        Press Esc to close
      </Typography>
    </Box>
  );
}
